import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { isDone } from '@opti-sea/risq';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, StatusBadge, usePositionNames } from '../../../ui/components';
import { formatDate } from '../../../ui/format';
import { useRpc, useVesselItems, useWeekly } from '../data';
import { itemsOfPosition, unfinishedFromLastWeek, weekStart } from '../model';

const MAX = 10;
const THIS_WEEK = 'this';
const NEXT_WEEK = 'next';

/**
 * D9 Questions of the week: vessel × position × week (this or next). 5–10 recommended, at most 10
 * (server rule); "Copy unfinished from last week" (Q8).
 */
export function WeeklyPage() {
  const { t } = useTranslation('dashboard');
  const { vessels, access } = useWorkplace();
  const { name } = usePositionNames();
  const [params, setParams] = useSearchParams();
  const vesselId = params.get('vessel') ?? vessels[0]?.id ?? '';
  const [position, setPosition] = useState<string>('CO');
  const [whichWeek, setWhichWeek] = useState<string>(THIS_WEEK);
  const now = new Date();
  const week = weekStart(now, whichWeek === THIS_WEEK ? 0 : 1);
  const lastWeek = weekStart(now, whichWeek === THIS_WEEK ? -1 : 0);
  const { items, loading, error } = useVesselItems(vesselId);
  const weekly = useWeekly(vesselId, [week, lastWeek]);
  const save = useRpc('risq_set_weekly_questions', [['weekly', vesselId]]);
  const departments = useMemo(
    () => new Map(access.positions.map((p) => [p.code, p.department])),
    [access.positions],
  );
  const candidates = useMemo(
    () => itemsOfPosition(items ?? [], position, (code) => departments.get(code)),
    [items, position, departments],
  );
  const current = (weekly.data ?? [])
    .filter((w) => w.week_start === week && w.position_code === position)
    .map((w) => w.item_id);
  const previous = (weekly.data ?? [])
    .filter((w) => w.week_start === lastWeek && w.position_code === position)
    .map((w) => w.item_id);
  // The draft belongs to one vessel, position, week and saved set; anything else starts afresh.
  const draftKey = [vesselId, position, week, ...current].join('|');
  const [draft, setDraft] = useState<{ key: string; ids: string[] } | null>(null);
  const chosen = draft !== null && draft.key === draftKey ? draft.ids : current;
  const setChosen = (ids: string[]) => setDraft({ key: draftKey, ids });
  const toggle = (id: string) =>
    setChosen(chosen.includes(id) ? chosen.filter((c) => c !== id) : [...chosen, id]);
  const changed = chosen.slice().sort().join() !== current.slice().sort().join();
  const weekOptions = [
    { value: THIS_WEEK, label: t('weekly.thisWeek', { date: formatDate(weekStart(now)) }) },
    { value: NEXT_WEEK, label: t('weekly.nextWeek', { date: formatDate(weekStart(now, 1)) }) },
  ];
  const open = candidates.filter((i) => !isDone(i.status) || chosen.includes(i.id));

  return (
    <>
      <PageTitle title={t('weekly.title')} />
      <Stack>
        <Group align="flex-end">
          <Select
            label={t('weekly.vessel')}
            data={vessels.map((v) => ({ value: v.id, label: v.name }))}
            value={vesselId}
            allowDeselect={false}
            onChange={(value) => value !== null && setParams({ vessel: value })}
          />
          <Select
            label={t('weekly.position')}
            data={access.positions.map((p) => ({ value: p.code, label: name(p.code) }))}
            value={position}
            allowDeselect={false}
            onChange={(value) => value !== null && setPosition(value)}
          />
          <SegmentedControl
            aria-label={t('weekly.week')}
            value={whichWeek}
            onChange={setWhichWeek}
            data={weekOptions}
          />
        </Group>
        <Text>{t('weekly.hint', { max: MAX })}</Text>
        <QueryState loading={loading || weekly.isPending} error={error ?? weekly.error}>
          {() => (
            <Stack>
              <Group>
                <Text fw={700}>{t('weekly.chosen', { count: chosen.length, max: MAX })}</Text>
                {chosen.length > 0 && chosen.length < 5 ? (
                  <Text c="orange.9">{t('weekly.fewHint')}</Text>
                ) : null}
              </Group>
              <Group>
                <Button
                  disabled={!changed || chosen.length > MAX}
                  loading={save.isPending}
                  onClick={() =>
                    save.mutate({
                      p_vessel_id: vesselId,
                      p_position_code: position,
                      p_week_start: week,
                      p_item_ids: chosen,
                    })
                  }
                >
                  {t('weekly.save')}
                </Button>
                <Button
                  variant="default"
                  disabled={previous.length === 0}
                  onClick={() =>
                    setChosen(
                      [
                        ...new Set([...chosen, ...unfinishedFromLastWeek(previous, candidates)]),
                      ].slice(0, MAX),
                    )
                  }
                >
                  {t('weekly.copy', { count: unfinishedFromLastWeek(previous, candidates).length })}
                </Button>
                {save.isSuccess && !changed ? <Text c="green.9">{t('common.saved')}</Text> : null}
              </Group>
              {save.error === null ? null : (
                <Alert color="red" role="alert">
                  {t('common.saveFailed', { message: save.error.message })}
                </Alert>
              )}
              <Table.ScrollContainer minWidth={700}>
                <Table striped verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('weekly.choose')}</Table.Th>
                      <Table.Th>{t('questions.code')}</Table.Th>
                      <Table.Th>{t('questions.text')}</Table.Th>
                      <Table.Th>{t('questions.status')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {open.map((i) => (
                      <Table.Tr key={i.id}>
                        <Table.Td>
                          <Checkbox
                            aria-label={t('weekly.chooseQuestion', { code: i.question.code })}
                            checked={chosen.includes(i.id)}
                            disabled={!chosen.includes(i.id) && chosen.length >= MAX}
                            onChange={() => toggle(i.id)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Anchor component={Link} to={`/vessels/${vesselId}/questions/${i.id}`}>
                            {i.question.code}
                          </Anchor>
                        </Table.Td>
                        <Table.Td maw={520}>
                          <Text size="sm" lineClamp={2}>
                            {i.question.textEn}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge status={i.status} />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          )}
        </QueryState>
      </Stack>
    </>
  );
}
